import React, { useEffect, useState } from "react";
import { useDisclosure } from "@jumbo/components/JumboDialog/hooks/useDisclosure";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import moment from "moment";

import Stack from "@mui/material/Stack";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";

import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import AddCoupon from "./add-coupon/index";
import Div from "@jumbo/shared/Div";
import {
  createCoupon,
  deleteCoupon,
  getCouponList,
  toggleCouponStatus,
  updateCoupon,
} from "app/redux/slice/couponSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";

const Coupon = () => {
  const { isOpen, onClose, onOpen, stageAction, actionProps } =
    useDisclosure();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const couponData = useSelector((state) => state?.coupon);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("All");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const {
    isOpen: isDelete,
    onClose: closeDelete,
    actionProps: deleteActionProps,
    stageAction: deleteStageAction,
  } = useDisclosure();
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "coupon" });
  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });
  const getPermission = () => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
    } else {
      let permissionValue = {
        createPermission: false,
        updatePermission: false,
        deletePermission: false,
      };
      const rolePermission = permissionData?.filter((item) => {
        return item?.moduleName?.includes("Coupon") && item;
      });
      rolePermission?.length &&
        rolePermission.forEach(function (num) {
          if (num?.name === "create-coupon") {
            permissionValue.createPermission = true;
          }
          if (num?.name === "delete-coupon") {
            permissionValue.deletePermission = true;
          }
          if (num?.name === "update-coupon") {
            permissionValue.updatePermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };
  useEffect(() => {
    getPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleStatus = async (row) => {
    const res = await dispatch(
      toggleCouponStatus({ id: row._id, status: !row.isActive }),
    ).unwrap();
    if (res?.status === 200) {
      getData();
    } else {
      enqueueSnackbar(res?.data?.responseMessage || "Update failed", {
        variant: "error",
      });
    }
  };

  const columns = [
    { id: "code", label: "Code", sort: true },
    {
      id: "visibility",
      label: "Visibility",
      render: (row) =>
        row.isGlobal !== false ? (
          <Chip size="small" label="Global" color="info" />
        ) : (
          <Chip
            size="small"
            label={`${row.assignedSuppliers?.length || 0} Supplier(s)`}
            color="secondary"
          />
        ),
    },
    {
      id: "discountValue",
      label: "Discount",
      render: (row) =>
        row.discountType === "percentage"
          ? `${row.discountValue}%`
          : `₹${row.discountValue}`,
    },
    {
      id: "minCartValue",
      label: "Min Cart Value",
      render: (row) => (row.minCartValue ? `₹${row.minCartValue}` : "—"),
    },
    {
      id: "expiryDate",
      label: "Expiry Date",
      render: (row) =>
        row.expiryDate ? moment(row.expiryDate).format("DD-MM-YYYY") : "—",
    },
    {
      id: "usedCount",
      label: "Usage",
      render: (row) =>
        row.usageLimit ? `${row.usedCount || 0} / ${row.usageLimit}` : `${row.usedCount || 0}`,
    },
    {
      id: "isActive",
      label: "Status",
      align: "center",
      minWidth: 100,
      render: (row) => (
        <Tooltip title={row.isActive !== false ? "Deactivate" : "Activate"}>
          <Switch
            checked={row.isActive !== false}
            disabled={!permission.updatePermission}
            onChange={() => handleToggleStatus(row)}
            color="success"
            size="small"
          />
        </Tooltip>
      ),
    },
    {
      id: "createdAt",
      label: "Created Date",
      sort: true,
      render: (row) => moment(row?.createdAt).format("DD-MM-YYYY"),
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      align: "right",
      render: (row) => (
        <>
          {permission.updatePermission && (
            <IconButton
              onClick={() => {
                stageAction(row);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {permission.deletePermission && (
            <IconButton
              onClick={() => {
                deleteStageAction(row);
              }}
            >
              <DeleteIcon color={"error"} />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  const handleSubmit = async (data) => {
    const newCouponData = {
      code: data.code?.trim()?.toUpperCase(),
      title: data.title?.trim() || null,
      description: data.description?.trim(),
      discountType: data.discountType,
      discountValue: Number(data.discountValue),
      minCartValue: data.minCartValue !== "" ? Number(data.minCartValue) : 0,
      maxDiscountAmount:
        data.maxDiscountAmount !== "" ? Number(data.maxDiscountAmount) : null,
      validFrom: data.validFrom || null,
      expiryDate: data.expiryDate || null,
      usageLimit: data.usageLimit !== "" ? Number(data.usageLimit) : null,
      assignedSuppliers: (data.assignedSuppliers || []).map((s) => s._id),
    };
    const response = await dispatch(
      actionProps?._id
        ? updateCoupon({ id: actionProps._id, data: newCouponData })
        : createCoupon(newCouponData),
    ).unwrap();
    if (response.status === 200) {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "success",
      });
      getData();
      onClose();
    } else {
      enqueueSnackbar(response.data?.responseMessage, {
        variant: "error",
      });
    }
  };

  const DeleteCoupon = () => {
    dispatch(deleteCoupon(deleteActionProps?._id))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "success",
          });
          getData();
          closeDelete();
        } else {
          enqueueSnackbar(res.data?.responseMessage, {
            variant: "error",
          });
          closeDelete();
        }
      });
  };

  const getData = () => {
    localStorage.setItem("coupon-search", searchText.trim());
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(status !== "All" && { isActive: status === "Active" }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getCouponList(value));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  return (
    <>
      {couponData.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => setSearchText("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={status}
                        label="Status"
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <MenuItem value={"All"}>All</MenuItem>
                        <MenuItem value={"Active"}>Active</MenuItem>
                        <MenuItem value={"Inactive"}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Coupons List
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={() => {
                        getData();
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  {permission.createPermission && (
                    <LoadingButton
                      onClick={() => {
                        onOpen();
                      }}
                      sx={{
                        mt: { xs: 1, md: 0 },
                      }}
                      type="submit"
                      variant="contained"
                    >
                      Add Coupon
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
              <Table
                columns={columns}
                rows={couponData.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={couponData.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                editAction={stageAction}
              />
            </CardContent>
          </Card>
          <DialogDefault
            open={isOpen}
            onClose={onClose}
            content={
              <AddCoupon
                value={actionProps}
                onClose={onClose}
                handleSubmit={handleSubmit}
              />
            }
            title={actionProps?._id ? "Update Coupon" : "Add Coupon"}
            fullWidth={true}
            maxWidth={"sm"}
            actions={<></>}
          />
          <DialogConfirm
            isOpen={isDelete}
            onClose={closeDelete}
            title="Are you sure you want to delete this Coupon?"
            onConfirm={DeleteCoupon}
            btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
          />
        </>
      )}
    </>
  );
};

export default Coupon;
